/**
 * GlobalActivityTicker - Shows scrolling site-wide activity feed
 * Displays non-personal events: achievements, milestones, readings, pet hatches, etc.
 *
 * Performance optimizations:
 *  - Pauses requestAnimationFrame when tab is hidden or ticker is offscreen
 *  - Pauses on hover
 *  - Skips polling while tab is hidden, refreshes on visibility return
 *  - Single 60s poll interval (cache TTL on backend is 30s)
 *  - GPU-accelerated translate3d
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Star, Sparkles, Egg, Swords, Trophy, Moon, Zap, Heart, Crown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 60000;

// Activity type configurations
const ACTIVITY_CONFIG = {
  achievement: { icon: Trophy, color: 'text-yellow-400', prefix: '🏆' },
  level_up: { icon: Zap, color: 'text-purple-400', prefix: '⚡' },
  pet_hatch: { icon: Egg, color: 'text-green-400', prefix: '🥚' },
  boss_kill: { icon: Swords, color: 'text-red-400', prefix: '⚔️' },
  reading: { icon: Moon, color: 'text-blue-400', prefix: '🔮' },
  reincarnation: { icon: Crown, color: 'text-amber-400', prefix: '♾️' },
  rare_drop: { icon: Sparkles, color: 'text-pink-400', prefix: '✨' },
  milestone: { icon: Star, color: 'text-cyan-400', prefix: '🌟' },
  donation: { icon: Heart, color: 'text-rose-400', prefix: '💖' },
};

// Generate placeholder activities when API is unavailable
const generatePlaceholderActivities = () => ([
  { type: 'milestone', message: 'CosmiCards has reached 40+ users!', time: 'recently' },
  { type: 'reading', message: 'A cosmic reading was completed', time: 'moments ago' },
  { type: 'achievement', message: 'Someone unlocked a rare badge', time: 'just now' },
  { type: 'pet_hatch', message: 'A mystical pet has hatched!', time: 'recently' },
  { type: 'level_up', message: 'A hero leveled up in Cosmic Quest', time: 'moments ago' },
  { type: 'boss_kill', message: 'A legendary boss was defeated!', time: 'just now' },
]);

export const GlobalActivityTicker = () => {
  const [activities, setActivities] = useState([]);
  const [isVisible, setIsVisible] = useState(true);
  const containerRef = useRef(null);
  const tickerRef = useRef(null);
  const animationRef = useRef(null);
  const positionRef = useRef(0);
  // Refs for live booleans avoid re-running animation effect on every change
  const isPausedRef = useRef(false);
  const isHiddenRef = useRef(typeof document !== 'undefined' && document.hidden);
  const isOnscreenRef = useRef(true);

  const fetchActivities = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/feed/global-feed`, { timeout: 5000 });
      if (response.data?.activities?.length > 0) {
        setActivities(response.data.activities);
      } else {
        setActivities(generatePlaceholderActivities());
      }
    } catch (error) {
      setActivities(generatePlaceholderActivities());
    }
  }, []);

  // Real-time feed via Server-Sent Events. Falls back to polling if EventSource
  // isn't supported or the stream errors out (e.g., proxy interference).
  useEffect(() => {
    fetchActivities();

    // Polling lifecycle (used both as fallback and after SSE failure)
    let pollInterval = null;
    const startPolling = () => {
      if (pollInterval) return;
      pollInterval = setInterval(fetchActivities, POLL_INTERVAL_MS);
    };
    const stopPolling = () => {
      if (pollInterval) { clearInterval(pollInterval); pollInterval = null; }
    };

    let es = null;
    const startSSE = () => {
      if (typeof EventSource === 'undefined' || document.hidden) return false;
      try {
        es = new EventSource(`${API}/api/feed/global-feed/stream`);
        es.addEventListener('snapshot', (evt) => {
          try {
            const data = JSON.parse(evt.data);
            if (data?.activities?.length > 0) setActivities(data.activities);
          } catch { /* ignore malformed event */ }
        });
        es.addEventListener('update', (evt) => {
          try {
            const data = JSON.parse(evt.data);
            if (data?.activities?.length > 0) setActivities(data.activities);
          } catch { /* ignore */ }
        });
        es.onerror = () => {
          // SSE failed — fall back to polling
          try { es && es.close(); } catch { /* ignore */ }
          es = null;
          if (!document.hidden) startPolling();
        };
        return true;
      } catch {
        return false;
      }
    };

    const stopSSE = () => {
      if (es) { try { es.close(); } catch { /* ignore */ } es = null; }
    };

    if (!document.hidden) {
      // Prefer SSE; fall back to polling if it doesn't start
      if (!startSSE()) startPolling();
    }

    const onVisibility = () => {
      isHiddenRef.current = document.hidden;
      if (document.hidden) {
        stopSSE();
        stopPolling();
      } else {
        fetchActivities();
        if (!startSSE()) startPolling();
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stopSSE();
      stopPolling();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchActivities]);

  // Offscreen detection — pause RAF when ticker scrolls out of viewport
  useEffect(() => {
    if (!containerRef.current || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        isOnscreenRef.current = entry.isIntersecting;
      },
      { threshold: 0.01 }
    );
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [isVisible]);

  // Smooth RAF-based animation for buttery smooth scrolling
  useEffect(() => {
    if (!tickerRef.current || activities.length === 0) return;

    const ticker = tickerRef.current;
    const speed = 0.5; // pixels per frame

    const animate = () => {
      // Skip frame entirely when paused/hidden/offscreen — saves CPU
      if (!isPausedRef.current && !isHiddenRef.current && isOnscreenRef.current) {
        positionRef.current -= speed;
        const halfWidth = ticker.scrollWidth / 2;
        if (Math.abs(positionRef.current) >= halfWidth) {
          positionRef.current = 0;
        }
        ticker.style.transform = `translate3d(${positionRef.current}px, 0, 0)`;
      }
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [activities]);

  if (!isVisible || activities.length === 0) return null;

  // Triple the activities for seamless infinite loop
  const displayActivities = [...activities, ...activities, ...activities];

  return (
    <div
      ref={containerRef}
      className="fixed bottom-0 left-0 right-0 z-40 bg-gradient-to-r from-black/95 via-babylon-obsidian/95 to-black/95 border-t border-babylon-gold/20 backdrop-blur-sm"
      onMouseEnter={() => { isPausedRef.current = true; }}
      onMouseLeave={() => { isPausedRef.current = false; }}
      data-testid="global-activity-ticker"
    >
      {/* Gradient fade edges */}
      <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-black to-transparent z-10 pointer-events-none" />
      <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-black to-transparent z-10 pointer-events-none" />

      {/* Close button */}
      <button
        onClick={() => setIsVisible(false)}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white text-xs px-2 py-1 z-20 transition-colors"
        title="Hide ticker"
        data-testid="ticker-close-btn"
      >
        ✕
      </button>

      {/* Ticker content with GPU-accelerated transform */}
      <div className="overflow-hidden py-2.5 mx-8">
        <div
          ref={tickerRef}
          className="flex whitespace-nowrap will-change-transform"
          style={{
            transform: 'translate3d(0, 0, 0)',
            backfaceVisibility: 'hidden',
            perspective: '1000px'
          }}
        >
          {displayActivities.map((activity, index) => {
            const config = ACTIVITY_CONFIG[activity.type] || ACTIVITY_CONFIG.milestone;
            const Icon = config.icon;

            return (
              <div
                key={index}
                className="inline-flex items-center mx-8 text-sm select-none"
              >
                <span className="mr-1.5 text-base">{config.prefix}</span>
                <Icon size={14} className={`mr-2 ${config.color} flex-shrink-0`} />
                {activity.username && activity.user_id ? (
                  <span className="text-gray-200 font-medium">
                    <a
                      href={`/profile/${activity.user_id}`}
                      className="text-babylon-gold hover:text-babylon-gold/80 underline decoration-babylon-gold/30 hover:decoration-babylon-gold/60 transition-colors cursor-pointer"
                      onClick={(e) => { e.stopPropagation(); }}
                      data-testid={`ticker-profile-${activity.user_id}`}
                    >
                      {activity.username}
                    </a>
                    {' '}{activity.message.replace(activity.username + ' ', '').replace(activity.username, '')}
                  </span>
                ) : (
                  <span className="text-gray-200 font-medium">{activity.message}</span>
                )}
                <span className="text-gray-500 text-xs ml-2 opacity-75">• {activity.time}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default GlobalActivityTicker;
